// @flow

import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import type {RecordFactory} from 'immutable';

import {fromJS, Map, Record, type RecordOf} from 'immutable';
import {getQueryParam} from 'app/utils/urls';
import {type StoreShapeType} from 'app/reducers/types';

export type ResponseInfoType = {
  count: number,
  next: null | string,
  num_pages: number,
  previous: null | string,
};

export type ResponseType<T> = ResponseInfoType & {
  results: Array<T>,
};

export type StoredResponseInfoType = {|
  count: number,
  next: null | number,
  previous: null | number,
  numPages: number,
|};

export type StoredResponseType<T: *> =
  | {|
      ...StoredResponseInfoType,
      loading: false,
      results: Array<T>,
    |}
  | {|
      count: number,
      loading: true,
      next: null | number,
      results: Array<T>,
    |};

export type RequestResponseMapType<T> = {
  byId: Map<string, T>,
  queries: Map<string, ResponseType<string>>,
};

export const getRequestResponseMapFactory = <T>(): RecordFactory<
  RequestResponseMapType<T>
> =>
  Record({
    byId: Map(),
    queries: Map(),
  })();

const filterUndefined = (obj) =>
  // removes params like {fields: undefined}
  omitBy(obj, isUndefined);

/* state getter */

export const getDenormalizedListFactory =
  <T>(
    dataGetter: (StoreShapeType) => any,
    postProcessor: (
      StoreShapeType,
      StoredResponseType<any>,
      queryStr: string
    ) => StoredResponseType<T> = (state, items) => items
  ) =>
  (state: StoreShapeType, query: {[string]: any}): StoredResponseType<any> => {
    const stateObj = dataGetter(state);
    const byIdMap = stateObj.get('byId');

    const queryStr = JSON.stringify(filterUndefined(query));

    const storedResponse = stateObj.getIn(['queries', queryStr]);

    if (storedResponse == null) {
      return {
        count: 1,
        loading: true,
        next: null,
        results: [],
      };
    }

    return postProcessor(
      state,
      storedResponse
        .set(
          'results',
          storedResponse.get('results').map((id) => byIdMap.get(id))
        )
        .toJS(),
      queryStr
    );
  };

export const setNormalizedList = <T: RecordOf<any> | Map<string, any>>(
  query: any,
  response: ResponseType<any>,
  state: T,
  idGetter: (any) => string = (i) => i.id.toString()
): T => {
  const key: string = JSON.stringify(query);
  return state
    .setIn(['queries', key], convertToIdList(response, idGetter))
    .set(
      'byId',
      updateObjectsMap(response.results, state.get('byId'), idGetter)
    );
};

const convertToIdList = (
  response: ResponseType<any>,
  idGetter: (any) => string
): StoredResponseType<any> =>
  (fromJS({
    loading: false,
    count: response.count,
    next: getQueryParam(response.next, 'page'),
    previous: getQueryParam(response.previous, 'page', '1'),
    numPages: response.num_pages,
    results: response.results.map(idGetter),
  }): any);

const updateObjectsMap = (
  results,
  currentState?: Map<string, string> | RecordOf<any>,
  idGetter: (any) => string
) =>
  currentState != null
    ? ((currentState.merge != null
        ? currentState
        : fromJS(currentState)): any).merge(
        (fromJS(
          results.reduce((newMap, item) => {
            newMap[idGetter(item)] = item;
            return newMap;
          }, {})
        ): any)
      )
    : null;
